/**
 * Single source of truth for the string and numeric literals that cross module
 * boundaries — API enum values, localStorage keys, route paths and paging sizes.
 *
 * These are contract values: the API decides them and several unrelated screens
 * compare against them. Spelled inline, a typo is a silent no-match at runtime
 * (`msg.type === 'TEXT '` renders nothing and throws nowhere), so they live here
 * and are imported by name.
 *
 * Values must match the backend exactly. When changing one, change it there
 * first — this file follows the server, never the other way round.
 */

/** `channel_type` on the create-channel payload. */
export const CHANNEL_TYPE = Object.freeze({
  CHAT: 'CHAT',
  GROUP: 'GROUP',
  // A thread hangs off one message of a group. The client never creates one by type —
  // POST /chat/create-thread does — but the saved-messages list has to recognise it to
  // label the source and route to the parent group.
  THREAD: 'THREAD',
  CHAT_SUPPORT: 'CHAT_SUPPORT',
});

/** `type` on a chat message. Decides which bubble body renders. */
export const MESSAGE_TYPE = Object.freeze({
  TEXT: 'TEXT',
  MEDIA: 'MEDIA',
  AUDIO: 'AUDIO',
  LOCATION: 'LOCATION',
});

/** `type` on a call — audio-only or with video. */
export const CALL_TYPE = Object.freeze({
  AUDIO: 'AUDIO',
  VIDEO: 'VIDEO',
});

/** `call_status` on a call-history record. */
export const CALL_STATUS = Object.freeze({
  INCOMING: 'INCOMING',
  OUTGOING: 'OUTGOING',
  MISSED: 'MISSED',
});

/** `method` on a call: 1:1, an ad-hoc set of people, or a group channel. */
export const CALL_METHOD = Object.freeze({
  SINGLE: 'SINGLE',
  MULTIPLE: 'MULTIPLE',
  GROUP: 'GROUP',
});

/**
 * Account tier. Only LEVEL3 accounts may hide their last-seen timestamp, which
 * is why the presence control is conditional.
 */
export const USER_LEVEL = Object.freeze({
  LEVEL1: 'LEVEL1',
  LEVEL2: 'LEVEL2',
  LEVEL3: 'LEVEL3',
});

/** Tier that gets the last-seen privacy toggle. */
export const LAST_SEEN_LEVEL = USER_LEVEL.LEVEL3;

/** `is_notification_enabled` is an int flag on the server, not a boolean. */
export const NOTIFICATION_FLAG = Object.freeze({
  ON: 1,
  OFF: 0,
});

/**
 * localStorage keys. Written and read from unrelated places (boot, the auth
 * interceptor, the theme switch, the app lock), which is exactly where a typo
 * costs the most.
 */
export const STORAGE_KEYS = Object.freeze({
  USER: 'user',
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  DARK_MODE: 'darkMode',
  IS_APP_LOCKED: 'isAppLocked',
  FCM_TOKEN: 'fcmToken',
  BACKGROUND_MESSAGE: 'backgroundMessage',
  /**
   * Where the visitor was heading before they were bounced to sign-in.
   *
   * `sessionStorage`, not `localStorage`: it is a one-shot handoff across a single
   * navigation, and a stale value surviving into next week's session would send someone
   * to a note they have long since stopped caring about.
   */
  POST_LOGIN_REDIRECT: 'postLoginRedirect',
});

/** Window event the rail's theme switch fires; App.js and useDarkModeValue listen. */
export const DARK_MODE_EVENT = 'darkModeToggled';

/** Route paths referenced from more than one module. */
export const ROUTES = Object.freeze({
  ROOT: '/',
  MESSAGES: '/messages',
  CALLS: '/calls/recent-calls',
  NOTIFICATIONS: '/notifications',
  SAVED: '/saved',
  NOTES: '/notes',
  NOTE: (id) => `/notes/${id}`,
  /**
   * The bare call route, kept as a legacy alias.
   *
   * A call now lives at `/calling/:id` — see `CALL_ROOM`. This path stays routable
   * because the service worker is registered without `skipWaiting`/`clients.claim`,
   * so after a deploy the OLD worker keeps using it as its notification click target
   * until every tab has closed. Reaching it resolves to the live call's id, or
   * redirects to /messages if there is no call.
   */
  CALLING: '/calling',
  SETTINGS: '/settings',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_SECURITY: '/settings/security',
  SETTINGS_SECURITY_PASSWORD: '/settings/security/password',
  SETTINGS_SECURITY_2FA: '/settings/security/2fa',
  SETTINGS_SECURITY_PASSCODE: '/settings/security/passcode',
  SETTINGS_SECURITY_DELETE: '/settings/security/delete',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_SUPPORT: '/settings/support',

  /**
   * Where a call actually lives.
   *
   * The id in the path is what lets a reloaded tab, or a notification click on a
   * cold start, find its way back into a call: Redux is memory and `sessionStorage`
   * could be wiped by the teardown that runs while the page unloads, but a URL
   * survives both. The id doubles as the Agora channel name, so one value in one
   * place identifies the call, the room and the API resource.
   */
  CALL_ROOM: (id) => `/calling/${id}`,
});

/** Conversation-list filter pills. */
export const CONVERSATION_FILTER = Object.freeze({
  ALL: 'all',
  UNREAD: 'unread',
  GROUPS: 'groups',
  CHANNELS: 'channels',
});

export const CHANNEL_KIND = Object.freeze({
  GROUP: 'GROUP',
  CHANNEL: 'CHANNEL',
});

export const MEMBER_ROLE = Object.freeze({
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
});

/** Reach of a message deletion. `ALL` is admin-only and server-enforced. */
export const DELETE_SCOPE = Object.freeze({
  SELF: 'self',
  ALL: 'all',
});

/**
 * Idle seconds before the app lock engages when the user has no stored value.
 * Mirrors the backend's `lock_time Int? @default(300)`, so the client and the
 * server agree on the fallback.
 */
export const DEFAULT_LOCK_SECONDS = 300;

/**
 * One page, for every list in the app.
 *
 * 20 rather than 10 because the page has to overflow the container it is rendered in,
 * or infinite scroll never starts: on a tall desktop pane ten rows do not reach the
 * bottom of the viewport, so no scroll is possible, so no scroll event fires, so the
 * second page is never requested and the list looks like it ends at ten.
 *
 * It is also the page size for every SUBSEQUENT read, not just the first. A first page
 * sized differently from the rest is a second number to keep in step with the server
 * for no benefit — and the server's own defaults (message history, saved messages,
 * notifications) are all 20 now.
 *
 * These used to be four independent literals — `PAGE_SIZE`, `SAVED_MESSAGES.PAGE_SIZE`,
 * an inline `limit=${10}` in the sagas, and a `+ 10` in each scroll handler — that all
 * had to agree for paging to be correct, with nothing enforcing that they did.
 */
export const PAGE_SIZE = 20;

/** Contacts in the new-conversation popup. Paged on scroll, like everything else. */
export const CONTACTS_PAGE_SIZE = PAGE_SIZE;

/** Members in a channel's details panel. Paged on scroll via `/chat/group/:id/members`. */
export const ROSTER_PAGE_SIZE = PAGE_SIZE;

/**
 * Saved messages. `PAGE_SIZE` matches the backend's
 * `constants.SAVED_MESSAGES.DEFAULT_PAGE_SIZE`; the server caps `limit` at 50 and
 * refuses anything larger, so keeping this in step is what stops the list asking for
 * a page it will not get. `MAX_PER_USER` mirrors the backend cap, for the copy that
 * tells the user how full their list is.
 */
export const SAVED_MESSAGES = Object.freeze({
  PAGE_SIZE,
  MAX_PER_USER: 1000,
  NOTE_MAX_LENGTH: 500,
});

/**
 * Notes.
 *
 * Every number here mirrors one in the backend's `constants.NOTES`. Keeping them in step
 * is what stops the client asking for a page the server will refuse, or letting someone
 * type past a limit only to have the save rejected.
 *
 * `COLORS` are TOKEN NAMES, never hex. The dark theme is a separate palette rather than a
 * darkened light one, so a hex value written into a document would be correct in one theme
 * and unreadable in the other — and unlike a UI colour it lives in user content, where it
 * could not be fixed later without a data migration. The tokens resolve to
 * `--note-<name>` in index.css.
 */
export const NOTES = Object.freeze({
  PAGE_SIZE,
  MAX_PER_USER: 500,
  TITLE_MAX_LENGTH: 200,
  TAG_NAME_MAX_LENGTH: 50,
  MAX_TAG_FILTER: 5,
  MAX_MEDIA_PER_NOTE: 50,
  COLORS: Object.freeze(['default', 'coral', 'amber', 'sage', 'sky', 'violet']),
  /**
   * How often the editor saves while a note is being edited.
   *
   * An INTERVAL, not a debounce. A debounce fires after typing stops, which means continuous
   * typing never saves and a pausing typist saves on every pause — both backwards. On a
   * clock, work is never more than one tick old however someone types, and the request rate
   * is bounded however fast they go.
   *
   * Five seconds is short enough that losing a tick costs a sentence, long enough that a
   * fast typist is not generating a request per word.
   */
  AUTOSAVE_INTERVAL_MS: 5000,
});

/**
 * Socket events for live co-editing. Mirrors `NOTE_COLLAB_EVENT` on the server.
 *
 * Literals in both places would be a silent failure rather than an error: a mistyped event
 * name means a client that connects, syncs nothing and reports no problem at all.
 */
export const NOTE_COLLAB_EVENT = Object.freeze({
  JOIN: 'note:join',
  LEAVE: 'note:leave',
  UPDATE: 'note:update',
  AWARENESS: 'note:awareness',
  AWARENESS_LEAVE: 'note:awareness-leave',
  CHECKPOINT: 'note:checkpoint',
  SAVED: 'note:saved',
  ACCESS_CHANGED: 'note:access-changed',
});

/**
 * The shared type inside the Y.Doc that holds the note body.
 *
 * Must equal `Y_FRAGMENT` in the backend's `src/shared/notePmSchema.js`. If the two ever
 * disagree the two ends bind to different fragments of the same document and neither sees
 * the other's edits — which presents as a broken socket rather than as a mismatch, so
 * `scripts/schemaDrift.mjs` asserts it.
 */
export const NOTE_Y_FIELD = 'default';

/** Connection states the editor surfaces to the writer. */
export const NOTE_COLLAB_STATUS = Object.freeze({
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  OFFLINE: 'offline',
  DENIED: 'denied',
});

/**
 * Colours for other people's carets.
 *
 * Picked from the theme's accent range rather than generated at random: a random hue lands
 * on the page background often enough to be a real problem, and two random hues are
 * regularly indistinguishable. Assigned by hashing the user id so one person keeps the same
 * colour across sessions and for every viewer.
 */
export const NOTE_CARET_COLORS = Object.freeze([
  '#ff6b6b',
  '#4dabf7',
  '#51cf66',
  '#fcc419',
  '#cc5de8',
  '#20c997',
  '#ff922b',
  '#5c7cfa',
]);

/** The notes sidebar's two primary tabs. Archived and Trash live behind the overflow. */
export const NOTE_TABS = Object.freeze({
  MINE: 'mine',
  SHARED: 'shared',
  ARCHIVED: 'archived',
  TRASH: 'trash',
});

/** Resolved role on a note, as the API reports it in `my_role`. */
export const NOTE_ROLE = Object.freeze({
  OWNER: 'OWNER',
  EDIT: 'EDIT',
  VIEW: 'VIEW',
});

/**
 * Distance from the far edge of the scroll container, in px, at which the next page is
 * requested.
 *
 * Not zero: on a fractional-DPI display `scrollTop` settles at values like 0.5 and an
 * exact comparison never holds, which is how the message streams could refuse to load
 * older messages at all on some machines. Large enough that the next page is usually in
 * hand before the reader arrives at the end.
 */
export const SCROLL_FETCH_MARGIN_PX = 240;

/** Debounce before a keystroke turns into a directory lookup. */
export const SEARCH_DEBOUNCE_MS = 450;

/**
 * Typing indicator lifetime.
 *
 * `TYPING_IDLE_MS` — how long after the last keystroke this client tells the
 * channel it has stopped. Nothing used to send that at all: `status: false` was
 * only emitted on send, so someone who typed and then stopped (or left the text
 * in the box and walked away) lit the other side's indicator permanently.
 *
 * `TYPING_EXPIRY_MS` — how long a received indicator survives without a fresh
 * signal. This is the one that has to hold, because "stopped typing" can never be
 * relied upon to arrive: the tab closes, the connection drops, the sender is an
 * older client that does not emit it. Deliberately longer than the idle window,
 * so the normal path is still the sender saying so and this is only the backstop.
 */
export const TYPING_IDLE_MS = 2200;
export const TYPING_EXPIRY_MS = 5000;

/**
 * How long the group pane waits after a presence change before re-asking the server how
 * many of a channel's members are online.
 *
 * The count cannot be derived client-side — the roster is paged, so a large channel's
 * members are mostly not in hand — which makes each refresh a round trip. A group of any
 * size produces a burst of `presence:changed` when people arrive in the morning, and one
 * request per event would be a storm for a number that is only ever read as a rough tally.
 */
export const PRESENCE_RECOUNT_MS = 1500;

/**
 * Rows the @mention picker will render for one query.
 *
 * The picker had no cap, so the first `@` typed in a large channel rendered the whole
 * membership — hundreds of avatars and names — into a scroll box eight rows tall. The
 * query still narrows against the FULL roster before this is applied, so nobody
 * becomes unreachable; one more character brings them into range.
 */
export const MENTION_RESULT_CAP = 50;

/**
 * Group create/edit field lengths. Kept in step with `validateCreateGroup` in
 * the backend's chat.validation.js so the form rejects before the request does.
 */
export const GROUP_LIMITS = Object.freeze({
  name: Object.freeze({ min: 5, max: 30 }),
  description: Object.freeze({ min: 5, max: 100 }),
});

/**
 * Length of a Synapse login / employee ID.
 *
 * `generateUniqueLoginId` on the backend mints exactly nine digits
 * (100000000–999999999), so every field that takes one caps there — the
 * contact-support form and the add-member-by-ID field both read this.
 */
export const LOGIN_ID_DIGITS = 9;

/**
 * Contact-support field lengths (the pre-auth form on the sign-in screen).
 *
 * `loginId.max` is 9 because `generateUniqueLoginId` mints exactly 9 digits;
 * `email.max` is the RFC 5321 address ceiling. `message.max` is a local cap —
 * `validateContactSupport` has no maximum, so without it the only stop is the
 * endpoint's 100 kB body limit, and anything past the TEXT column behind
 * `chats.message` fails as a generic 500 instead of a field error.
 */
export const SUPPORT_LIMITS = Object.freeze({
  email: Object.freeze({ max: 254 }),
  loginId: Object.freeze({ max: LOGIN_ID_DIGITS }),
  message: Object.freeze({ min: 10, max: 2000 }),
});

/**
 * Abuse report. The min is there because "spam" tells a moderator nothing they
 * can act on; the max matches the backend's report validation.
 */
export const REPORT_LIMITS = Object.freeze({ description: Object.freeze({ min: 10, max: 300 }) });

/**
 * Scheduled message.
 *
 * `leadMs` is the floor on how far ahead a message may be scheduled. Zero would
 * accept a time already in the past by the time the request lands, and the cron
 * that delivers these runs every minute — anything under a minute is a promise
 * the backend cannot keep.
 */
export const SCHEDULE_LIMITS = Object.freeze({
  message: Object.freeze({ min: 1, max: 2000 }),
  leadMs: 60 * 1000,
});

/** Upload ceiling for a group picture, in bytes. */
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/** Human-readable form of MAX_AVATAR_BYTES for copy, so the two never drift. */
export const MAX_AVATAR_LABEL = `${MAX_AVATAR_BYTES / (1024 * 1024)} MB`;

const Constants = {
  CHANNEL_TYPE,
  MESSAGE_TYPE,
  CALL_TYPE,
  CALL_STATUS,
  CALL_METHOD,
  USER_LEVEL,
  LAST_SEEN_LEVEL,
  NOTIFICATION_FLAG,
  STORAGE_KEYS,
  DARK_MODE_EVENT,
  ROUTES,
  CONVERSATION_FILTER,
  PAGE_SIZE,
  DEFAULT_LOCK_SECONDS,
  CONTACTS_PAGE_SIZE,
  ROSTER_PAGE_SIZE,
  SCROLL_FETCH_MARGIN_PX,
  SAVED_MESSAGES,
  NOTES,
  NOTE_TABS,
  NOTE_COLLAB_EVENT,
  NOTE_COLLAB_STATUS,
  NOTE_Y_FIELD,
  NOTE_CARET_COLORS,
  NOTE_ROLE,
  SEARCH_DEBOUNCE_MS,
  TYPING_IDLE_MS,
  TYPING_EXPIRY_MS,
  PRESENCE_RECOUNT_MS,
  MENTION_RESULT_CAP,
  GROUP_LIMITS,
  LOGIN_ID_DIGITS,
  SUPPORT_LIMITS,
  REPORT_LIMITS,
  SCHEDULE_LIMITS,
  MAX_AVATAR_BYTES,
  MAX_AVATAR_LABEL,
};

export default Constants;
